import { arrayMove } from '@dnd-kit/sortable';
import { pointerWithin, rectIntersection } from '@dnd-kit/core';
/**
 * Calculates the depth of a node in the tree by traversing up via parentId
 * @param nodeId - ID of the node to calculate depth for
 * @param allNodes - Flat array of all nodes in the tree
 * @param currentDepth - Current depth (used for recursion)
 * @returns Depth level (1 = root, 2 = first child, etc.)
 *
 * @example
 * // For a tree: Root -> Child -> Grandchild
 * getNodeDepth('root-id', allNodes) // returns 1
 * getNodeDepth('child-id', allNodes) // returns 2
 * getNodeDepth('grandchild-id', allNodes) // returns 3
 */
export function getNodeDepth(nodeId, allNodes, currentDepth = 1) {
    const findNode = (nodes) => {
        for (const node of nodes) {
            if (node.id === nodeId)
                return node;
            if (node.children && node.children.length > 0) {
                const found = findNode(node.children);
                if (found)
                    return found;
            }
        }
        return undefined;
    };
    const node = findNode(allNodes);
    if (!node || !node.parentId)
        return currentDepth;
    return getNodeDepth(node.parentId, allNodes, currentDepth + 1);
}
/**
 * Calculates the maximum depth of a subtree (how many levels deep it goes)
 * @param node - Root node of the subtree
 * @returns Maximum depth of subtree (0 = no children, 1 = has children, etc.)
 *
 * @example
 * // For a node with 2 levels of children below it:
 * getSubtreeDepth(node) // returns 2
 */
export function getSubtreeDepth(node) {
    if (!node.children || node.children.length === 0)
        return 0;
    const childDepths = node.children.map(child => getSubtreeDepth(child));
    return 1 + Math.max(...childDepths);
}
/**
 * Checks if a node is a descendant of another node
 * @param childId - Potential descendant node ID
 * @param ancestorId - Potential ancestor node ID
 * @param allNodes - All nodes in the tree
 * @returns true if childId is a descendant of ancestorId
 *
 * @example
 * // Prevents circular references when moving nodes
 * isDescendant('child-id', 'grandchild-id', allNodes) // false
 * isDescendant('grandchild-id', 'child-id', allNodes) // true
 */
export function isDescendant(childId, ancestorId, allNodes) {
    const findNode = (nodes, id) => {
        for (const node of nodes) {
            if (node.id === id)
                return node;
            if (node.children && node.children.length > 0) {
                const found = findNode(node.children, id);
                if (found)
                    return found;
            }
        }
        return undefined;
    };
    const childNode = findNode(allNodes, childId);
    if (!childNode || !childNode.parentId)
        return false;
    if (childNode.parentId === ancestorId)
        return true;
    return isDescendant(childNode.parentId, ancestorId, allNodes);
}
/**
 * Validates if a node can be dropped into another node (for hierarchy changes)
 *
 * Validates:
 * 1. Can't drop into itself
 * 2. Can't create circular references (drop parent into descendant)
 * 3. Can't drop into disabled nodes
 * 4. Respects maximum depth restrictions
 *
 * @param draggedNode - Node being dragged
 * @param targetNode - Node being dropped onto
 * @param allNodes - All nodes in the tree
 * @param maxDepth - Maximum allowed depth (1 = root only, 2 = root + 1 level, etc.)
 * @returns true if drop is valid
 *
 * @example
 * canDropIntoNode(draggedNode, targetNode, allNodes, 3)
 * // Returns false if drop would exceed 3 levels deep
 */
export function canDropIntoNode(draggedNode, targetNode, allNodes, maxDepth) {
    // Can't drop into itself
    if (draggedNode.id === targetNode.id)
        return false;
    // Can't drop parent into its own descendant (prevents circular references)
    if (isDescendant(targetNode.id, draggedNode.id, allNodes))
        return false;
    // Can't drop into disabled nodes
    if (targetNode.disableNode)
        return false;
    // Check depth restrictions
    if (maxDepth !== undefined) {
        const targetDepth = getNodeDepth(targetNode.id, allNodes);
        const subtreeDepth = getSubtreeDepth(draggedNode);
        const resultingDepth = targetDepth + subtreeDepth + 1;
        if (resultingDepth > maxDepth)
            return false;
    }
    return true;
}
/**
 * Reorders nodes at the same level using drag & drop
 *
 * Uses @dnd-kit's arrayMove for efficient array reordering while
 * maintaining React key stability.
 *
 * @param nodes - Array of nodes at the same level
 * @param activeId - ID of node being dragged
 * @param overId - ID of node being dropped onto
 * @returns New array with reordered nodes
 *
 * @example
 * const reordered = reorderNodesAtLevel(siblings, 'node-2', 'node-4')
 * // Moves 'node-2' to the position of 'node-4'
 */
export function reorderNodesAtLevel(nodes, activeId, overId) {
    const oldIndex = nodes.findIndex(n => n.id === activeId);
    const newIndex = nodes.findIndex(n => n.id === overId);
    if (oldIndex === -1 || newIndex === -1)
        return nodes;
    return arrayMove(nodes, oldIndex, newIndex);
}
/**
 * Custom collision detection for nested tree structures.
 * Prioritizes pointer position to handle nested parent/child droppable zones.
 */
export const customCollisionDetection = (args) => {
    // First check pointer collisions (most precise)
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
        return pointerCollisions;
    }
    // Fallback to rectangle intersection
    return rectIntersection(args);
};
